/**
 * ReviewCampaign
 *
 * Final step showing a read-only summary of campaign data, namespace
 * information, media, and questions before create/update.
 */
import React, { useState, useEffect } from 'react';
import { Image, Building, Info, Shield, Loader2 } from 'lucide-react';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';
import { useNamespace } from '../../../context/namespace';

export function ReviewCampaign({ 
  formData, 
  surveyQuestions, 
  selectedTheme, 
  previewImage,
  hasExplainerVideo,
  explainerVideo,
  themes,
  className = '',
  currentNamespace,
  userPermission: propUserPermission, // Rename to avoid conflict with context
  isUpdateMode = false,
  submitButtonText = 'Create Campaign'
}) {
  // Log that the Review Campaign component is being rendered
  useEffect(() => {
    console.log('ReviewCampaign component rendered with submitButtonText:', submitButtonText);
  }, [submitButtonText]);

  const { namespaces, userPermission: contextUserPermission } = useNamespace();
  const [namespaceInfo, setNamespaceInfo] = useState(null);
  const [isLoadingNamespace, setIsLoadingNamespace] = useState(false);
  const [namespaceError, setNamespaceError] = useState(null);
  
  // Get the current namespace ID from namespaces
  const getCurrentNamespaceId = () => {
    // If currentNamespace is already an ID (from settings flow), use it directly
    if (currentNamespace && typeof currentNamespace === 'string' && currentNamespace.match(/^[a-zA-Z0-9-_]{20,}$/)) {
      console.log('currentNamespace appears to be an ID already:', currentNamespace);
      return currentNamespace;
    }
    
    // Otherwise, look up the ID by name
    if (!namespaces || namespaces.length === 0) return null;
    const currentNamespaceObj = namespaces.find(ns => ns.name === currentNamespace);
    return currentNamespaceObj?.id || null;
  };
  
  // Use the permission from props if provided, otherwise from context
  const effectiveUserPermission = propUserPermission || contextUserPermission;
  
  const namespaceId = getCurrentNamespaceId();
  
  // Log the current state for debugging
  useEffect(() => {
    console.log('ReviewCampaign namespaces:', {
      namespaces,
      currentNamespace,
      namespaceId,
      effectiveUserPermission
    });
  }, [namespaces, currentNamespace, namespaceId, effectiveUserPermission]);

  // Fetch namespace information when component mounts
  useEffect(() => {
    const fetchNamespaceInfo = async () => {
      if (!namespaceId) {
        console.error('No namespace ID available');
        setNamespaceError('Namespace ID not available');
        return;
      }
      
      // Check if we already have this namespace's info in the namespaces array
      const existingNamespace = namespaces.find(ns => ns.id === namespaceId);
      if (existingNamespace) {
        console.log('Using existing namespace info from context:', existingNamespace);
        setNamespaceInfo(existingNamespace);
        setIsLoadingNamespace(false);
        return;
      }
      
      console.log('Fetching namespace information for ID:', namespaceId);
      setIsLoadingNamespace(true);
      setNamespaceError(null);
      
      try {
        // Get the authentication token
        const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        if (!idToken) {
          console.error('No authentication token available');
          setNamespaceError('Authentication error');
          setIsLoadingNamespace(false);
          return;
        }
        
        console.log('Making request to fetch namespaces from /namespaces/my');
        
        // Fetch namespace information from the correct endpoint
        const response = await fetch(`${SERVER_URL}/namespaces/my`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        
        if (!response.ok) {
          console.error('Failed to fetch namespaces from /namespaces/my:', response.status, response.statusText);
          throw new Error(`Failed to fetch namespaces from /namespaces/my: ${response.status}`);
        }
        
        const namespaces = await response.json();
        console.log('Fetched namespaces:', namespaces.length, namespaces);
        
        // Find the current namespace in the list using the namespace ID
        const namespace = namespaces.find(ns => ns.id === namespaceId);
        
        if (namespace) {
          console.log('Found namespace:', namespace);
          setNamespaceInfo(namespace);
        } else {
          console.warn(`Namespace with ID ${namespaceId} not found in list of ${namespaces.length} namespaces`);
          setNamespaceError('Namespace not found');
        }
      } catch (error) {
        console.error('Error fetching namespace information:', error);
        setNamespaceError(error.message || 'Failed to fetch namespace information');
      } finally {
        setIsLoadingNamespace(false);
      }
    };
    
    fetchNamespaceInfo();
  }, [namespaceId]);

  // Get theme name for display
  const getThemeName = () => {
    if (selectedTheme === 'custom') {
      return 'Custom Theme';
    }
    return themes[selectedTheme]?.name || selectedTheme.charAt(0).toUpperCase() + selectedTheme.slice(1);
  };



  // Helper function to display permission in a user-friendly way
  const formatPermission = (permission) => {
    if (!permission) return 'No Access';
    
    switch(permission) {
      case 'admin':
        return 'Admin (Full Access)';
      case 'read/write':
        return 'Editor (Read/Write)';
      case 'readonly':
        return 'Viewer (Read Only)';
      default:
        return permission;
    }
  };

  // Get user permission - Updated to use multiple sources with priority
  const getUserPermission = () => {
    // If namespaceInfo has currentUserPermission, use that
    if (namespaceInfo?.currentUserPermission) {
      console.log('Using currentUserPermission from namespaceInfo:', namespaceInfo.currentUserPermission);
      return namespaceInfo.currentUserPermission;
    }
    
    // If we have a user permission from props or context, use that
    if (effectiveUserPermission) {
      console.log('Using effectiveUserPermission:', effectiveUserPermission);
      return effectiveUserPermission;
    }
    
    // Otherwise, try to find user permission in namespace members
    if (namespaceInfo && namespaceInfo.members && auth.currentUser) {
      const userEmail = auth.currentUser.email;
      const member = namespaceInfo.members.find(m => m.email === userEmail);
      if (member) {
        console.log('Found user in namespace members:', member);
        return member.permission;
      }
    }
    
    console.log('Using default permission: read');
    return 'read'; // Default fallback
  };
  
  // Get namespace display name
  const getNamespaceDisplayName = () => {
    if (namespaceInfo?.name) {
      return namespaceInfo.name;
    }
    
    // If namespace info is not available, try to format the ID to be more readable
    if (currentNamespace) {
      // If it looks like a UUID or Firebase ID, just show the first 8 characters
      if (currentNamespace.length > 20) {
        return `${currentNamespace.substring(0, 8)}...`;
      }
      return currentNamespace;
    }
    
    return 'Unknown Namespace';
  };

  // Format error message for display
  const formatErrorMessage = (error) => {
    if (!error) return 'Unknown error';
    
    if (error.includes('Authentication')) {
      return 'Authentication error. Please sign in again.';
    }
    
    if (error.includes('Failed to fetch')) {
      return 'Network error. Please check your connection.';
    }
    
    if (error.includes('namespaces/my')) {
      return 'Could not retrieve namespace information.';
    }
    
    if (error.includes('not found')) {
      return 'Namespace not found.';
    }
    
    return error;
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {isUpdateMode ? "Review Your Campaign Updates" : "Review Your Campaign"}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {isUpdateMode 
            ? "Please review all information before updating your campaign."
            : "Please review all information before creating your campaign."}
        </p>
      </div>

      {/* Namespace Information */}
      {currentNamespace && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Namespace</h4>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {isLoadingNamespace ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading namespace information...</span>
              </div>
            ) : namespaceError ? (
              <div className="flex items-center gap-2 text-red-500 dark:text-red-400">
                <Info className="h-4 w-4" />
                <span className="text-sm">Error: {formatErrorMessage(namespaceError)}</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-primary-text-600 dark:text-primary-text-400" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {getNamespaceDisplayName()}
                    </span>
                  </div>
                  {namespaceInfo?.description && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-7 mt-1">
                      {namespaceInfo.description}
                    </span>
                  )}
                </div>
                {getUserPermission() && (
                  <div className="flex items-center gap-2 px-2 py-1 bg-primary-100 dark:bg-primary-900/50 rounded-md mt-3 sm:mt-0 self-start">
                    <Shield className="h-3.5 w-3.5 text-primary-text-600 dark:text-primary-text-400" />
                    <span className="text-xs font-medium text-primary-700 dark:text-primary-300">
                      {formatPermission(getUserPermission())}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Campaign Information */}
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Campaign Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-4">
            <div className="space-y-4 md:space-y-2">
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1.5 md:mb-1">Internal Name</span>
                <span className="text-sm text-gray-900 dark:text-white">{formData.name || 'Not provided'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1.5 md:mb-1">Campaign Title</span>
                <span className="text-sm text-gray-900 dark:text-white">{formData.title || 'Not provided'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1.5 md:mb-1">Campaign Description</span>
                <span className="text-sm text-gray-900 dark:text-white">{formData.description || 'Not provided'}</span>
              </div>
            </div>
            <div className="space-y-4 md:space-y-2">
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 block mb-1.5 md:mb-1">Theme</span>
                <span className="text-sm text-gray-900 dark:text-white">{getThemeName()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Media */}
        <div className="h-px w-full bg-gray-200 dark:bg-gray-700 my-6"></div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Media</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 block mb-2">Campaign Image</span>
              {previewImage ? (
                <div className="relative w-full max-w-[210px] h-[118px] overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
                  <img 
                    src={previewImage} 
                    alt="Campaign" 
                    className="w-full h-full object-cover" 
                  />
                </div>
              ) : (
                <div className="relative w-full max-w-[210px] h-[118px] flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <Image className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 block mb-2">Explainer Video</span>
              {hasExplainerVideo && explainerVideo ? (
                <div className="relative w-full max-w-[210px] h-[118px] overflow-hidden rounded-md border border-gray-200 dark:border-gray-700 flex items-center justify-center">
                  {typeof explainerVideo === 'string' && explainerVideo.startsWith('http') ? (
                    <iframe 
                      src={explainerVideo.replace('youtube.com/watch?v=', 'youtube.com/embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full" 
                      allowFullScreen
                      title="YouTube Explainer Video"
                    />
                  ) : (
                    <video 
                      src={explainerVideo} 
                      className="w-full h-full object-cover" 
                    />
                  )}
                </div>
              ) : (
                <div className="relative w-full max-w-[210px] h-[118px] flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-gray-300 dark:text-gray-600">
                    <polygon points="23 7 16 12 23 17 23 7" />
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Survey Questions */}
        <div className="h-px w-full bg-gray-200 dark:bg-gray-700 my-6"></div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Survey Questions</h4>
          {surveyQuestions && surveyQuestions.length > 0 ? (
            <div className="space-y-5 sm:space-y-4">
              {surveyQuestions.map((q, index) => (
                <div key={q.id} className="p-4 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <span className="text-xs text-gray-500 dark:text-gray-400 block mb-2 sm:mb-1">Question {index + 1}</span>
                  <span className="text-sm text-gray-900 dark:text-white">{q.question || 'Empty question'}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 sm:p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-sm text-gray-500 dark:text-gray-400">No survey questions added</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 